import React, { useMemo } from "react";
import { StyleSheet } from "react-native";

import { match } from "@stepapp/core";
import { gradientPoint, palette, spacing } from "../styles";
import Block from "./Block";
import Gradient from "./Gradient";

type Props = {
  cap: number;
  value: number;
  width?: number;
  height?: number;
  type?: "default" | "pink";
  gradient?: boolean;
};

const ProgressBar = ({
  cap,
  value,
  height,
  width = spacing.unit * 13,
  type = "default",
  gradient = false,
}: Props) => {
  const activeWidth = useMemo(() => {
    return width > 0 ? (value / cap) * width : 0;
  }, [cap, value, width]);

  const customHeight = useMemo(() => {
    return height ? height : width / 10;
  }, [height, width]);

  const colors = match(type)
    .with("pink", () => ({
      bgColor: palette.common.white,
      progressBarColor: palette.secondary.wrongPink,
      gradientPoints: gradientPoint.pink,
    }))
    .otherwise(() => ({
      bgColor: palette.font.placeholder,
      progressBarColor: palette.primary.accent,
      gradientPoints: gradientPoint.primary,
    }));

  return (
    <Block borderRadius={2} style={[styles.container, { width }]}>
      <Block style={{ backgroundColor: colors.bgColor, width, height: customHeight }} />

      {gradient ? (
        <Gradient
          direction="topRight"
          height={8}
          points={colors.gradientPoints}
          style={[styles.gradient, { width: activeWidth }]}
        />
      ) : (
        <Block
          style={[
            styles.activeProgressContainer,
            { backgroundColor: colors.progressBarColor, width: activeWidth, height: customHeight },
          ]}
        />
      )}
    </Block>
  );
};

export default React.memo(ProgressBar);

const styles = StyleSheet.create({
  activeProgressContainer: {
    elevation: 1,
    position: "absolute",
    zIndex: 1,
  },
  container: {
    overflow: "hidden",
  },
  gradient: {
    borderRadius: spacing.radius,
  },
});
